import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { SEO, generateServiceSchema, generateLocalBusinessSchema } from '../components/SEO';
import { CTASection } from '../components/CTASection';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { FAQSection } from '../components/FAQSection';
import { TestimonialCard } from '../components/TestimonialCard';
import { Breadcrumb } from '../components/Breadcrumb';
import { TrustSection, ProfessionalEnvironment } from '../components/TrustBuilding';
import { services, faqs, testimonials, galleryImages, neighborhoods } from '../data/mock';
import { Check, Clock, ArrowRight, MapPin } from 'lucide-react';
import { slugify } from '../utils/seoHelpers';

export const ServicePage = () => {
  const location = useLocation();
  
  // Extract slug from pathname
  const pathname = location?.pathname || '';
  const pathSegments = pathname.split('/').filter(Boolean);
  const slug = pathSegments[pathSegments.length - 1] || '';
  
  const service = services.find(s => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Serviço não encontrado</h1>
          <Link to="/" className="text-[#00B4D8] hover:underline">
            Voltar para home
          </Link>
        </div>
      </div>
    );
  }

  // Generate both LocalBusiness and Service schemas
  const businessSchema = generateLocalBusinessSchema({
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": testimonials.length,
      "bestRating": "5",
      "worstRating": "1"
    }
  });

  const serviceSchema = generateServiceSchema(
    service.name,
    service.description,
    service.duration,
    {
      "offers": {
        "@type": "Offer",
        "priceCurrency": "BRL",
        "price": "0",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "BRL"
        },
        "availability": "https://schema.org/InStock"
      }
    }
  );

  // Combine both schemas in an array
  const schemaData = [businessSchema, serviceSchema];

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Serviços', link: '/#servicos' },
    { label: service.name }
  ];

  // Select 8 neighborhoods for display
  const displayNeighborhoods = neighborhoods.slice(0, 8);
  
  // Related services (exclude current)
  const relatedServices = services.filter(s => s.id !== service.id).slice(0, 3);

  return (
    <>
      <SEO 
        title={`${service.name} em Goiânia e Aparecida de Goiânia | Box7go`}
        description={`${service.description} Agende agora na Box7go e garanta qualidade premium com produtos profissionais e equipe especializada.`}
        keywords={`${service.name.toLowerCase()}, ${slug}, lava jato goiania, estética automotiva goiania, ${service.name.toLowerCase()} aparecida de goiania`}
        schemaData={schemaData}
      />

      {/* Breadcrumb */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Hero */}
      <section className="relative min-h-[400px] flex items-center bg-black">
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${service.image})`,
            opacity: 0.5
          }}
        ></div>
        <div className="container mx-auto px-4 z-10 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {service.name}
            </h1>
            <p className="text-xl text-white/90 mb-8">
              {service.description}
            </p>
            <WhatsAppButton 
              text="Solicitar Orçamento"
              className="text-lg px-8 py-4"
            />
          </div>
        </div>
      </section>

      {/* Detalhes do Serviço */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  O que está incluso
                </h2>
                <ul className="space-y-4">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#00B4D8] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="bg-gray-50 rounded-xl p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Clock className="w-8 h-8 text-[#00B4D8]" />
                    <div>
                      <p className="text-sm text-gray-600">Duração estimada</p>
                      <p className="text-2xl font-bold text-gray-900">{service.duration}</p>
                    </div>
                  </div>
                  <div className="border-t pt-6">
                    <h3 className="font-bold text-gray-900 mb-4">Como funciona?</h3>
                    <ol className="space-y-3">
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-[#00B4D8] text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">1</span>
                        <span className="text-gray-700">Entre em contato pelo WhatsApp</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-[#00B4D8] text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">2</span>
                        <span className="text-gray-700">Agende seu horário</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-[#00B4D8] text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">3</span>
                        <span className="text-gray-700">Deixe seu carro conosco</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-[#00B4D8] text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">4</span>
                        <span className="text-gray-700">Retire impecável!</span>
                      </li>
                    </ol>
                  </div>
                  <div className="mt-8">
                    <WhatsAppButton 
                      text="Agendar este Serviço"
                      className="w-full justify-center"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <TrustSection serviceType={service.name} />

      {/* Professional Environment */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <ProfessionalEnvironment />
          </div>
        </div>
      </section>

      {/* Galeria */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Confira nosso trabalho
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {galleryImages.slice(0, 6).map((image, index) => (
              <div key={index} className="relative overflow-hidden rounded-lg shadow-md aspect-square">
                <img 
                  src={image} 
                  alt={`Trabalho ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <WhatsAppButton 
              text="Agendar agora no WhatsApp"
              messageType="booking"
              serviceName={service.name}
              variant="large"
            />
          </div>
        </div>
      </section>

      {/* Avaliações */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Clientes satisfeitos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <TestimonialCard key={index} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* Bairros Atendidos - SEO Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Bairros Atendidos em Goiânia e Aparecida de Goiânia
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Oferecemos {service.name.toLowerCase()} com qualidade premium em diversos bairros da região
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {displayNeighborhoods.map((neighborhood) => (
              <Link
                key={neighborhood}
                to={`/lava-jato-${slugify(neighborhood)}`}
                className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg hover:bg-[#00B4D8] hover:text-white transition-all duration-300 group shadow-sm hover:shadow-md"
              >
                <MapPin className="w-4 h-4 flex-shrink-0 text-[#00B4D8] group-hover:text-white" />
                <span className="text-sm font-medium">{neighborhood}</span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/lava-jato-perto-de-mim"
              className="inline-flex items-center gap-2 text-[#00B4D8] font-semibold hover:text-[#0096C7] transition-colors text-lg"
            >
              Ver todos os bairros atendidos
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Veja Também - Cities and Related */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Veja Também
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Link
              to="/lava-jato-goiania"
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h3 className="font-bold text-xl text-gray-900 mb-2">Lava Jato Goiânia</h3>
              <p className="text-gray-600 mb-4">
                Todos os serviços de lava jato disponíveis em Goiânia
              </p>
              <span className="inline-flex items-center gap-2 text-[#00B4D8] font-semibold">
                Ver mais
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link
              to="/lava-jato-aparecida-de-goiania"
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h3 className="font-bold text-xl text-gray-900 mb-2">Lava Jato Aparecida de Goiânia</h3>
              <p className="text-gray-600 mb-4">
                Serviços de lavagem em Aparecida de Goiânia
              </p>
              <span className="inline-flex items-center gap-2 text-[#00B4D8] font-semibold">
                Ver mais
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link
              to="/estetica-automotiva-goiania"
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h3 className="font-bold text-xl text-gray-900 mb-2">Estética Automotiva Goiânia</h3>
              <p className="text-gray-600 mb-4">
                Detalhamento premium em Goiânia
              </p>
              <span className="inline-flex items-center gap-2 text-[#00B4D8] font-semibold">
                Ver mais
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Outros Serviços */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Outros serviços que você pode gostar
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {relatedServices.map((otherService) => (
              <Link
                key={otherService.id}
                to={`/${otherService.slug}`}
                className="bg-gray-50 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="relative h-40">
                  <img 
                    src={otherService.image} 
                    alt={otherService.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 mb-2">{otherService.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{otherService.shortDesc}</p>
                  <span className="inline-flex items-center gap-2 text-[#00B4D8] font-semibold text-sm">
                    Ver detalhes
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection faqs={faqs} />

      {/* CTA */}
      <CTASection 
        title={`Agende sua ${service.name}`}
        subtitle="Fale conosco pelo WhatsApp e garanta seu horário"
      />
    </>
  );
};
